import React from 'react'
import { css } from '@emotion/react'
import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import { Card, colors, font, mq } from 'plume-ui'
import { notPublishedYet } from '@/lib/api/contentful'
import { trimTextForNewsPicks } from '@/lib/featured'
import { articlePath, ARTICLE_HREF } from '@/lib/url'
import { Article } from '../../../types'
import { LockIcon } from '../articles/LockIcon'
import { TagButton, TagButtonGroup } from '../../atoms/TagButton'
import { PublishedDate } from '../../common/PublishedDate'

type Props = {
  article: Article
  index: number
  className?: string
}

export const FeaturedCard: React.FC<Props> = (props) => {
  const linkProps = {
    as: articlePath(props.article),
    href: ARTICLE_HREF,
  }
  return notPublishedYet(props.article.publishedAt) ? (
    <Card
      css={css`
        display: flex;
        padding: 16px 24px;
        transition: 0.3s;
        height: 100%;
        margin-top: 8px;
        ${mq.large} {
          flex-direction: column;
          margin: 0;
          padding: 0;
        }
      `}
      className={props.className}
    >
      <Card.Image
        src={'/static/images/coming-soon-ogp.jpg'}
        css={css`
          height: 100%;
          figure {
            line-height: 0.93;
            width: 112px !important;
            ${mq.large} {
              width: 100% !important;
            }
          }
        `}
      />
      <Card.Content
        css={css`
          && {
            display: flex;
            flex-direction: column;
            padding: 0 0 0 8px;
            ${mq.large} {
              padding: 8px 0 0;
            }
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <Title
            article={props.article}
            index={props.index}
            clickable={false}
            comingSoon
          ></Title>
        </div>
      </Card.Content>
    </Card>
  ) : (
    <Card
      css={css`
        display: flex;
        flex-direction: column;
        padding: 20px;
        transition: 0.3s;
        height: 100%;
        border-top: 8px solid ${colors.gray40};
        ${mq.large} {
          padding: 0;
          border-top: 0;
        }
      `}
      className={props.className}
    >
      <Link {...linkProps}>
        <a
          css={css`
            height: 100%;
          `}
        >
          <div
            css={css`
              position: relative;
              ::after {
                content: '';
                position: absolute;
                left: 0;
                top: 0;
                display: block;
                width: 100%;
                height: 100%;
                background-color: #000;
                opacity: 0;
                transition: opacity 0.2s ease;
              }
              :hover {
                ::after {
                  opacity: 0.08;
                }
              }
            `}
          >
            <Card.Image
              src={props.article.headerImage.url}
              css={css`
                figure {
                  line-height: 0.93;
                }
              `}
            />
          </div>
        </a>
      </Link>
      <Card.Content
        css={css`
          && {
            display: flex;
            flex-direction: column;
            padding: 16px 0 0;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <div>
            <PublishedDate
              css={css`
                color: ${colors.textLight};
              `}
            >
              {props.article.publishedAt}
            </PublishedDate>
          </div>
          <Link {...linkProps}>
            <a>
              <Title
                article={props.article}
                index={props.index}
                clickable={true}
              ></Title>
              <div
                css={css`
                  font-size: 13px;
                  line-height: 1.5;
                  letter-spacing: 0.02em;
                  color: ${colors.black30};
                  max-height: calc(14px * 1.5 * 4);
                  -webkit-line-clamp: 4;
                  display: -webkit-box;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  margin-bottom: 8px;
                  ${mq.large} {
                    font-size: 14px;
                  }
                `}
              >
                <Markdown
                  options={{
                    overrides: {
                      blockquote: (props: any) => <span {...props} />,
                      p: (props: any) => <span {...props} />,
                      a: (props: any) => (
                        <span {...props}>{props.children}</span>
                      ),
                      img: () => null,
                      iframe: () => null,
                    },
                  }}
                >
                  {trimTextForNewsPicks(props.article.introduction)}
                </Markdown>
              </div>
            </a>
          </Link>
        </div>
        {props.article.newTags && (
          <TagButtonGroup>
            {props.article.newTags.map((tag, i) => (
              <TagButton tag={tag} key={i} />
            ))}
          </TagButtonGroup>
        )}
      </Card.Content>
    </Card>
  )
}

const Title: React.FC<{
  article: Article
  index: number
  clickable: boolean
  comingSoon?: boolean
}> = (props) => (
  <div
    css={css`
      display: inline-block;
      margin: 8px 0;
        ${
          props.comingSoon &&
          `
            ${mq.small}{
              margin: 0;
            }
          `
        }
      \`}
    `}
  >
    <span
      css={css`
        color: ${colors.textStrong};
        font-size: 16px;
        font-weight: ${font.weight.bold};
        line-height: 1.5;
        letter-spacing: 0.02em;
        transition: border-color 0.2s linear;
        border-bottom: 1px solid transparent;
        &:hover {
          border-bottom: ${props.clickable
            ? `1px solid ${colors.textStrong}`
            : '1px solid transparent'};
        }
        ${mq.large} {
          font-size: 20px;
        }
        ${props.comingSoon &&
        `
            ${mq.small}{
              font-size: 15px;
            }
          `}
      `}
    >
      <LockIcon article={props.article} />
      {props.article.title}
    </span>
  </div>
)
